import { routes } from '@core/config/routes'

// Public slug (what appears in the URL) → the `LegalDoc.key` the Help module
// publishes the document under. Adding a public legal page means adding a row
// here, a `LEGAL_*` path constant, and a seed doc in `helpSeedData.ts`.
export const LEGAL_DOC_KEY_BY_SLUG = {
  terms: 'terms_and_conditions',
  privacy: 'privacy_policy',
  regulatory: 'regulatory_information',
} as const

export type LegalDocSlug = keyof typeof LEGAL_DOC_KEY_BY_SLUG

export function isLegalDocSlug(value: string): value is LegalDocSlug {
  return value in LEGAL_DOC_KEY_BY_SLUG
}

// Reverse of the map above, derived rather than restated so the two can't drift.
const SLUG_BY_DOC_KEY: Record<string, LegalDocSlug> = Object.fromEntries(
  Object.entries(LEGAL_DOC_KEY_BY_SLUG).map(([slug, key]) => [key, slug as LegalDocSlug]),
)

/**
 * Public viewer path for a document key, or `null` when the document has no
 * public page — admin-added documents (via "Add Document") never get one.
 */
export function legalDocPathForKey(key: string): string | null {
  const slug = SLUG_BY_DOC_KEY[key]
  return slug ? routes.LEGAL_DOC.replace(':docSlug', slug) : null
}

export interface LegalFooterLink {
  label: string
  to: string
}

// Consumed by the auth-page footers (login / verify OTP). Those render before
// the operator has a token, so they must point at the PUBLIC read-only viewers —
// they cannot link into `/help`, which is auth-gated.
export const LEGAL_FOOTER_LINKS: readonly LegalFooterLink[] = [
  { label: 'Terms', to: routes.LEGAL_TERMS },
  { label: 'Privacy', to: routes.LEGAL_PRIVACY },
  { label: 'Regulatory', to: routes.LEGAL_REGULATORY },
] as const

// Consumed by the app-shell footer, which only renders for a signed-in operator.
// There both links open the Legal & Compliance CMS, where the documents are
// managed — not the read-only viewer. Key these by `label`, not `to`: both rows
// share the same destination.
export const ADMIN_LEGAL_FOOTER_LINKS: readonly LegalFooterLink[] = [
  { label: 'Terms', to: routes.HELP },
  { label: 'Privacy', to: routes.HELP },
] as const
